import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Alert, Button, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Divider, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import { LABELS } from '../../utils/labels';
import VehicleConflictDialog from './VehicleConflictDialog';

const VehicleEdit = () => {
  const { number } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vehicle, setVehicle] = useState(null);

  const [form, setForm] = useState({
    license_plate: '', vin: '', make: '', model: '', year: '', assignedUserId: ''
  });
  const [originalAssignedUserId, setOriginalAssignedUserId] = useState('');
  const [branchUsers, setBranchUsers] = useState([]);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', body: '', action: null });
  const [transferOpen, setTransferOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [targetBranch, setTargetBranch] = useState('');

  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [pendingUpdate, setPendingUpdate] = useState(null);

  const loadVehicle = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getVehicleByNumber(number);
      setVehicle(data);
      setForm({
        license_plate: data.license_plate || '',
        vin: data.vin || '',
        make: data.make || '',
        model: data.model || '',
        year: data.year || '',
        assignedUserId: data.assigned_user_id ? String(data.assigned_user_id) : ''
      });
      setOriginalAssignedUserId(data.assigned_user_id ? String(data.assigned_user_id) : '');

      const [users, allBranches] = await Promise.all([
        apiService.getUsersByBranch(data.branch),
        apiService.getBranches()
      ]);
      setBranchUsers(users || []);
      setBranches(allBranches || []);
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to load vehicle'));
    } finally {
      setLoading(false);
    }
  }, [number]);

  useEffect(() => { loadVehicle(); }, [loadVehicle]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const driverChanged = form.assignedUserId !== originalAssignedUserId;

  const getReassignConfirmMessage = () => {
    if (!driverChanged) return null;
    const newUser = branchUsers.find((u) => String(u.id) === form.assignedUserId);
    const oldUser = branchUsers.find((u) => String(u.id) === originalAssignedUserId);

    if (!form.assignedUserId) {
      return `This will remove ${oldUser?.username || 'the current driver'} from unit ${vehicle.number}. They will have no vehicle assigned.`;
    }
    if (newUser?.vehicle_number && newUser.vehicle_number !== vehicle.number) {
      return `${newUser.username} is currently assigned to unit ${newUser.vehicle_number}. Reassigning will remove them from that vehicle. Continue?`;
    }
    if (oldUser && form.assignedUserId) {
      return `This will reassign unit ${vehicle.number} from ${oldUser.username} to ${newUser?.username || 'the selected driver'}.`;
    }
    return `This will assign ${newUser?.username || 'the selected driver'} to unit ${vehicle.number}.`;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (driverChanged) {
      setConfirmDialog({
        open: true,
        title: 'Confirm Driver Change',
        body: getReassignConfirmMessage(),
        action: () => doSave()
      });
      return;
    }
    await doSave();
  };

  const doSave = async () => {
    setConfirmDialog({ open: false, title: '', body: '', action: null });
    setSaving(true);
    setError('');
    try {
      const updateData = {
        license_plate: form.license_plate || null,
        vin: form.vin || null,
        make: form.make || null,
        model: form.model || null,
        year: form.year ? Number(form.year) : null,
      };

      const duplicateCheckData = {
        license_plate: form.license_plate || null,
        vin: form.vin || null,
      };
      const duplicateCheck = await apiService.checkVehicleDuplicates(vehicle.number, duplicateCheckData);
      if (duplicateCheck.conflicts && duplicateCheck.conflicts.length > 0) {
        setPendingUpdate({ updateData, driverChanged });
        setConflicts(duplicateCheck.conflicts);
        setConflictDialogOpen(true);
        setSaving(false);
        return;
      }

      await apiService.updateVehicle(vehicle.number, updateData);

      if (driverChanged) {
        if (!form.assignedUserId) {
          await apiService.assignVehicle(vehicle.number, {
            userId: null,
            strategy: 'unassign'
          });
        } else {
          const newUser = branchUsers.find((u) => String(u.id) === form.assignedUserId);
          const strategy = (newUser?.vehicle_number && newUser.vehicle_number !== vehicle.number)
            ? 'swap' : 'fail_on_conflict';
          await apiService.assignVehicle(vehicle.number, {
            userId: Number(form.assignedUserId),
            strategy
          });
        }
      }

      setSuccess('Vehicle updated successfully.');
      await loadVehicle();
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to save changes'));
    } finally {
      setSaving(false);
    }
  };

  const handleConflictReassign = async (conflictsToReassign) => {
    setSaving(true);
    try {
      const { updateData, driverChanged: needsAssign } = pendingUpdate;
      const firstConflict = conflictsToReassign[0];
      if (firstConflict) {
        await apiService.reassignVehicleData(vehicle.number, {
          conflicts: conflictsToReassign,
          sourceNumber: firstConflict.conflictingVehicle.number
        });
      }
      await apiService.updateVehicle(vehicle.number, updateData);

      if (needsAssign && form.assignedUserId) {
        const newUser = branchUsers.find((u) => String(u.id) === form.assignedUserId);
        const strategy = (newUser?.vehicle_number && newUser.vehicle_number !== vehicle.number)
          ? 'swap' : 'fail_on_conflict';
        await apiService.assignVehicle(vehicle.number, {
          userId: Number(form.assignedUserId),
          strategy
        });
      }

      setConflictDialogOpen(false);
      setPendingUpdate(null);
      setConflicts([]);
      setSuccess('Vehicle updated successfully.');
      await loadVehicle();
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to save changes'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusAction = (nextStatus) => {
    const messages = {
      inactive: `This will mark unit ${vehicle.number} as Inactive. It will no longer appear as available.`,
      sold: `This will mark unit ${vehicle.number} as Sold and unassign any driver. This is permanent.`,
      active: `This will reactivate unit ${vehicle.number}.`
    };
    setConfirmDialog({
      open: true,
      title: `Mark as ${nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}?`,
      body: messages[nextStatus],
      action: () => doStatusChange(nextStatus)
    });
  };

  const doStatusChange = async (nextStatus) => {
    setConfirmDialog({ open: false, title: '', body: '', action: null });
    setSaving(true);
    setError('');
    try {
      await apiService.updateVehicleStatus(vehicle.number, nextStatus);
      setSuccess(`Vehicle marked as ${nextStatus}.`);
      await loadVehicle();
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to update status'));
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!targetBranch) return;
    setSaving(true);
    setError('');
    setTransferOpen(false);
    try {
      await apiService.transferVehicle(vehicle.number, {
        targetBranch,
        targetUserId: null,
        makeActive: true,
        strategy: 'fail_on_conflict'
      });
      setSuccess(`Vehicle transferred to ${targetBranch}.`);
      await loadVehicle();
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Failed to transfer vehicle'));
    } finally {
      setSaving(false);
    }
  };

  if (!user?.is_admin) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
        <Alert severity="warning">Admin access required.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !vehicle) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/driver-register')} sx={{ mb: 2 }}>
          Back to list
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!vehicle) return null;

  const currentStatus = vehicle.status || 'active';

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/driver-register')}>
          Back
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Edit Unit {vehicle.number}
        </Typography>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Vehicle Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label={LABELS.licensePlate} fullWidth
              value={form.license_plate} onChange={handleChange('license_plate')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label={LABELS.vin} fullWidth
              value={form.vin} onChange={handleChange('vin')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label={LABELS.make} fullWidth
              value={form.make} onChange={handleChange('make')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label={LABELS.model} fullWidth
              value={form.model} onChange={handleChange('model')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label={LABELS.year} type="number" fullWidth
              value={form.year} onChange={handleChange('year')} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Assigned Driver
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="driver-select-label">Driver</InputLabel>
          <Select
            labelId="driver-select-label"
            label="Driver"
            value={form.assignedUserId}
            onChange={handleChange('assignedUserId')}
          >
            <MenuItem value="">
              <em>None (Unassigned)</em>
            </MenuItem>
            {branchUsers.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.username} ({u.email})
                {u.vehicle_number && u.vehicle_number !== vehicle.number
                  ? ` — currently on ${u.vehicle_number}`
                  : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {driverChanged && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {getReassignConfirmMessage()}
          </Alert>
        )}
      </Paper>

      {/* Status & Actions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Status & Actions
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body2">Current status:</Typography>
          <Chip
            label={currentStatus}
            size="small"
            color={currentStatus === 'active' ? 'success' : currentStatus === 'inactive' ? 'warning' : 'error'}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {currentStatus !== 'inactive' && (
            <Button variant="outlined" color="warning" onClick={() => handleStatusAction('inactive')} disabled={saving}>
              Mark Inactive
            </Button>
          )}
          {currentStatus !== 'active' && (
            <Button variant="outlined" color="success" onClick={() => handleStatusAction('active')} disabled={saving}>
              Reactivate
            </Button>
          )}
          {currentStatus !== 'sold' && (
            <Button variant="outlined" color="error" onClick={() => handleStatusAction('sold')} disabled={saving}>
              Mark as Sold
            </Button>
          )}
          <Button variant="outlined" onClick={() => { setTargetBranch(''); setTransferOpen(true); }} disabled={saving}>
            Transfer to Branch
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, title: '', body: '', action: null })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, title: '', body: '', action: null })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmDialog.action} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Transfer to Branch</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="transfer-branch-label">Branch</InputLabel>
            <Select
              labelId="transfer-branch-label"
              label="Branch"
              value={targetBranch}
              onChange={(e) => setTargetBranch(e.target.value)}
            >
              {branches
                .filter((b) => b.name !== vehicle.branch)
                .map((b) => (
                  <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTransfer} disabled={!targetBranch}>
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      <VehicleConflictDialog
        open={conflictDialogOpen}
        onClose={() => { setConflictDialogOpen(false); setPendingUpdate(null); setConflicts([]); }}
        conflicts={conflicts}
        targetVehicle={vehicle.number}
        onReassign={handleConflictReassign}
        loading={saving}
      />
    </Box>
  );
};

export default VehicleEdit;
